/**
 * Product Form Data Schema
 * Defines the structure for all form fields and their properties
 */

export const PRODUCT_FORM_SCHEMA = {
  // Basic Product Information
  code: {
    type: "string",
    label: "BarCode/Rbarcode",
    required: true,
    autoFocus: true,
    fieldType: "select"
  },
  
  category: {
    type: "string",
    label: "Category",
    required: true,
    fieldType: "select"
  },
  
  metal_type: {
    type: "string",
    label: "Metal Type",
    required: true,
    fieldType: "select"
  },
  
  product_name: {
    type: "string",
    label: "Sub Category",
    required: true,
    fieldType: "select"
  },
  
  design_name: {
    type: "string",
    label: "Product Design Name",
    required: false,
    fieldType: "select"
  },
  
  pricing: {
    type: "string",
    label: "Pricing",
    required: true,
    fieldType: "select",
    defaultValue: "By Weight",
    options: [
      { value: "By Weight", label: "By Weight" },
      { value: "By fixed", label: "By fixed" }
    ]
  },

  // Fixed Pricing Fields (shown when pricing === "By fixed")
  printing_purity: {
    type: "string",
    label: "Printing Purity",
    required: false,
    fieldType: "text",
    conditional: { field: "pricing", value: "By fixed" }
  },
  
  pieace_cost: {
    type: "number",
    label: "Piece Cost",
    required: false,
    fieldType: "number",
    conditional: { field: "pricing", value: "By fixed" }
  },
  
  qty: {
    type: "number",
    label: "Qty",
    required: true,
    fieldType: "number",
    defaultValue: "1"
  },
  
  piece_taxable_amt: {
    type: "number",
    label: "Taxable Amt",
    required: false,
    fieldType: "number",
    readOnly: true,
    calculated: true,
    conditional: { field: "pricing", value: "By fixed" }
  },
  
  mrp_price: {
    type: "number",
    label: "MRP",
    required: false,
    fieldType: "number",
    calculated: true,
    conditional: { field: "pricing", value: "By fixed" }
  },

  // Weight-based Pricing Fields (shown when pricing !== "By fixed")
  selling_purity: {
    type: "string",
    label: "Selling Purity",
    required: false,
    fieldType: "select",
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  custom_purity: {
    type: "string",
    label: "Custom Purity %",
    required: false,
    fieldType: "text",
    conditional: { field: "selling_purity", value: "Manual" }
  },
  
  gross_weight: {
    type: "number",
    label: "Gross Wt",
    required: false,
    fieldType: "number",
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  stone_weight: {
    type: "number",
    label: "Stone Wt",
    required: false,
    fieldType: "number",
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  stone_price: {
    type: "number",
    label: "St Price",
    required: false,
    fieldType: "number",
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  weight_bw: {
    type: "number",
    label: "Weight BW",
    required: false,
    fieldType: "number",
    readOnly: true,
    calculated: true,
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  va_on: {
    type: "string",
    label: "Wastage On",
    required: false,
    fieldType: "select",
    defaultValue: "Gross Weight",
    options: [
      { value: "Gross Weight", label: "Gross Weight" },
      { value: "Weight BW", label: "Weight BW" }
    ],
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  va_percent: {
    type: "number",
    label: "Wastage%",
    required: false,
    fieldType: "number",
    defaultValue: "0",
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  wastage_weight: {
    type: "number",
    label: "W.Wt",
    required: false,
    fieldType: "number",
    readOnly: true,
    calculated: true,
    defaultValue: "0.00",
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  total_weight_av: {
    type: "number",
    label: "Total Weight AW",
    required: false,
    fieldType: "number",
    readOnly: true,
    calculated: true,
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  rate: {
    type: "number",
    label: "Rate",
    required: false,
    fieldType: "number",
    conditional: { field: "pricing", value: "By Weight" }
  },
  
  rate_amt: {
    type: "number",
    label: "Amount",
    required: false,
    fieldType: "number",
    readOnly: true,
    calculated: true,
    defaultValue: "0.00"
  },
  
  mc_on: {
    type: "string",
    label: "MC On",
    required: false,
    fieldType: "select",
    defaultValue: "MC %",
    options: [
      { value: "MC / Gram", label: "MC / Gram" },
      { value: "MC / Piece", label: "MC / Piece" },
      { value: "MC %", label: "MC %" }
    ]
  },
  
  mc_per_gram: {
    type: "number",
    label: "MC/Gm",
    required: false,
    fieldType: "number",
    readOnly: false
  },
  
  making_charges: {
    type: "number",
    label: "Total MC",
    required: false,
    fieldType: "number",
    calculated: true
  },
  
  hm_charges: {
    type: "number",
    label: "HM Charge",
    required: false,
    fieldType: "number",
    defaultValue: "60.00"
  },

  // Tax and Pricing
  tax_percent: {
    type: "string",
    label: "Tax%",
    required: true,
    fieldType: "text",
    defaultValue: "03% GST"
  },
  
  tax_amt: {
    type: "number",
    label: "Tax Amt",
    required: false,
    fieldType: "number",
    readOnly: true,
    calculated: true,
    defaultValue: "0.00"
  },
  
  total_price: {
    type: "number",
    label: "Total Price",
    required: false,
    fieldType: "number",
    calculated: true,
    defaultValue: "0.00"
  },

  // Additional Information
  remarks_type: {
    type: "string",
    label: "Remarks Type",
    required: false,
    fieldType: "select",
    options: [
      { value: "Client Facing", label: "Client Facing" },
      { value: "Internal Facing", label: "Internal Facing" }
    ]
  },
  
  remarks: {
    type: "string",
    label: "Remarks",
    required: false,
    fieldType: "textarea"
  },

  // Image Handling
  product_image: {
    type: "file",
    label: "Product Image",
    required: false,
    fieldType: "file",
    accept: "image/*"
  },
  
  imagePreview: {
    type: "string",
    label: "Image Preview",
    required: false,
    fieldType: "image",
    readOnly: true
  },

  // System Fields
  transaction_status: {
    type: "string",
    label: "Transaction Status",
    required: false,
    fieldType: "hidden",
    defaultValue: "Sales"
  },
  
  sale_status: {
    type: "string",
    label: "Sale Status",
    required: false,
    fieldType: "hidden",
    defaultValue: "Delivered"
  },
  
  estimate_status: {
    type: "string",
    label: "Estimate Status",
    required: false,
    fieldType: "hidden",
    defaultValue: "Not Delivered"
  },
  
  opentag_id: {
    type: "string",
    label: "Open Tag ID",
    required: false,
    fieldType: "hidden"
  },

  // Offer-related fields (calculated)
  festival_discount: {
    type: "number",
    label: "Festival Discount",
    required: false,
    fieldType: "hidden",
    calculated: true
  },
  
  festival_discount_percentage: {
    type: "number",
    label: "Festival Discount Percentage",
    required: false,
    fieldType: "hidden",
    calculated: true
  },
  
  festival_discount_on_rate: {
    type: "number",
    label: "Festival Discount on Rate",
    required: false,
    fieldType: "hidden",
    calculated: true
  },
  
  original_total_price: {
    type: "number",
    label: "Original Total Price",
    required: false,
    fieldType: "hidden",
    calculated: true
  },
  
  original_piece_taxable_amt: {
    type: "number",
    label: "Original Piece Taxable Amount",
    required: false,
    fieldType: "hidden",
    calculated: true
  }
};

/**
 * Default product form data structure
 */
export const DEFAULT_PRODUCT_FORM_DATA = {
  // Basic Product Information
  code: "",
  category: "",
  metal_type: "",
  product_name: "",
  design_name: "",
  pricing: "By Weight",
  pricingOpt:"Net Weight",
  
  // Fixed Pricing Fields
  printing_purity: "",
  pieace_cost: "",
  qty: "1",
  piece_taxable_amt: "",
  mrp_price: "",
  
  // Weight-based Pricing Fields
  selling_purity: "",
  custom_purity: "",
  gross_weight: "",
  stone_weight: "",
  stone_price: "",
  weight_bw: "",
  va_on: "Gross Weight",
  va_percent: "0",
  wastage_weight: "0.00",
  total_weight_av: "",
  rate: "",
  rate_amt: "0.00",
  
  // Making Charges
  mc_on: "MC %",
  mc_per_gram: "",
  making_charges: "",
  hm_charges: "60.00",
  
  // Tax and Pricing
  tax_percent: "03% GST",
  tax_amt: "0.00",
  total_price: "0.00",
  
  // Additional Information
  remarks_type: "",
  remarks: "",
  
  // Image Handling
  product_image: null,
  imagePreview: null,
  
  // System Fields
  transaction_status: "Sales",
  sale_status: "Delivered",
  estimate_status: "Not Delivered",
  opentag_id: "",
  
  // Offer-related fields
  festival_discount: "",
  festival_discount_percentage: "",
  festival_discount_on_rate: "",
  original_total_price: "",
  original_piece_taxable_amt: ""
};

/**
 * Validation rules for form fields
 */
export const VALIDATION_RULES = {
  code: {
    required: "Barcode is required",
    minLength: { value: 1, message: "Barcode must be at least 1 character" }
  },
  
  category: {
    required: "Category is required"
  },
  
  metal_type: {
    required: "Metal type is required"
  },
  
  product_name: {
    required: "Product name is required"
  },
  
  qty: {
    required: "Quantity is required",
    min: { value: 1, message: "Quantity must be at least 1" },
    pattern: { value: /^\d+(\.\d+)?$/, message: "Quantity must be a valid number" }
  },
  
  gross_weight: {
    min: { value: 0, message: "Gross weight cannot be negative" },
    pattern: { value: /^\d+(\.\d+)?$/, message: "Gross weight must be a valid number" }
  },
  
  stone_weight: {
    min: { value: 0, message: "Stone weight cannot be negative" },
    pattern: { value: /^\d+(\.\d+)?$/, message: "Stone weight must be a valid number" }
  },
  
  rate: {
    min: { value: 0, message: "Rate cannot be negative" },
    pattern: { value: /^\d+(\.\d+)?$/, message: "Rate must be a valid number" }
  },
  
  pieace_cost: {
    min: { value: 0, message: "Piece cost cannot be negative" },
    pattern: { value: /^\d+(\.\d+)?$/, message: "Piece cost must be a valid number" }
  }
};

/**
 * Helper function to get field configuration
 */
export const getFieldConfig = (fieldName) => {
  return PRODUCT_FORM_SCHEMA[fieldName] || null;
};

/**
 * Helper function to check if field should be shown based on conditions
 */
export const shouldShowField = (fieldName, productFormData) => {
  const fieldConfig = getFieldConfig(fieldName);
  if (!fieldConfig || !fieldConfig.conditional) return true;
  
  const { field, value } = fieldConfig.conditional;
  return productFormData[field] === value;
};

/**
 * Helper function to get all visible fields for current form state
 */
export const getVisibleFields = (productFormData) => {
  return Object.keys(PRODUCT_FORM_SCHEMA).filter(fieldName => 
    shouldShowField(fieldName, productFormData)
  );
};

/**
 * Helper function to validate product form data
 */
export const validateProductFormData = (productFormData) => {
  const errors = {};
  
  Object.keys(VALIDATION_RULES).forEach(fieldName => {
    const rules = VALIDATION_RULES[fieldName];
    const value = productFormData[fieldName];
    
    // Required validation
    if (rules.required && (!value || value.toString().trim() === "")) {
      errors[fieldName] = rules.required;
      return;
    }
    
    // Min length validation
    if (rules.minLength && value && value.toString().length < rules.minLength.value) {
      errors[fieldName] = rules.minLength.message;
      return;
    }
    
    // Min value validation
    if (rules.min && value !== "" && parseFloat(value) < rules.min.value) {
      errors[fieldName] = rules.min.message;
      return;
    }
    
    // Pattern validation
    if (rules.pattern && value && !rules.pattern.value.test(value.toString())) {
      errors[fieldName] = rules.pattern.message;
      return;
    }
  });
  
  return errors;
}; 